import { useEffect, useRef } from 'react'
import { contactApi } from '@/lib/api/contacts.api'
import { entityApi } from '@/lib/api/entities.api'
import { memberApi } from '@/lib/api/members.api'
import { projectsApi } from '@/lib/api/projects.api'
import { tasksApi } from '@/lib/api/tasks.api'
import { createLogger } from '@/lib/logger'
import { isPopupWindow } from '@/lib/utils/tauri'
import { useAuth } from '@/store/auth.store'
import { useOrganizationStore } from '@/store/organization.store'
import { useQueryClient } from '@tanstack/react-query'

import { TaskWithAssignments } from '@scrambl/shared'

import { queryKeys } from './query-keys'
import { getMyTasksQueryKey } from './useMyTasksData'

const logger = createLogger('hooks/useDataPrefetch')

/**
 * Core module data prefetches — fired once after org is selected.
 * These are the list queries that back the main navigation modules.
 * Each prefetch populates the React Query cache so navigating to
 * that module renders instantly (no loading spinner).
 *
 * Prefetches run sequentially with idle scheduling to avoid
 * competing with the user's active page for bandwidth.
 */
interface PrefetchEntry {
  id: string
  queryKey: readonly unknown[]
  queryFn: () => Promise<unknown>
  staleTime: number
}

function buildPrefetches(
  userId: string,
  organizationId: string,
  pathname: string,
): PrefetchEntry[] {
  const isMyTasksRoute =
    pathname.includes('/my-tasks') || pathname.includes('/tasks/mine')
  const isProjectRoute = pathname.includes('/projects/')
  const isTaskRoute = pathname.includes('/tasks/')
  const isContactsRoute = pathname.includes('/contacts')

  return [
    // My Tasks (landing page for many users)
    // Uses same query key shape as useMyTasksData hook. Must match the
    // includeCompleted:true variant the My Work page requests — a different
    // key here means the page fetches its own 500-row copy on top of this one.
    {
      id: 'my-tasks',
      queryKey: getMyTasksQueryKey({
        userId,
        includeCompleted: true,
        organizationId,
      }),
      queryFn: async () => {
        const response = await tasksApi.getTasks({
          page: 1,
          limit: 500,
          sortBy: 'updatedAt',
          sortOrder: 'desc',
          assigneeIds: [userId],
          excludeCompleted: false,
          includePersonalTasks: true,
          includeSubtasks: true,
          view: 'list',
        } as Record<string, unknown>)

        // Match useMyTasksData cache shape exactly.
        return {
          tasks: response.data || ([] as TaskWithAssignments[]),
          totalCount: response.meta?.totalItems || 0,
        }
      },
      staleTime: 30_000,
    },
    // Projects list
    {
      id: 'projects',
      queryKey: queryKeys.projects.list(),
      queryFn: () => projectsApi.getProjects({ includeCustomFields: true }),
      staleTime: 60_000,
    },
    // Members / Team
    {
      id: 'members',
      queryKey: queryKeys.members.display(),
      queryFn: () => memberApi.getMembersForDisplay({ limit: 100 }),
      staleTime: 120_000,
    },
    // Contacts
    {
      id: 'contacts',
      queryKey: queryKeys.contacts.list(),
      queryFn: () => contactApi.getContacts({ limit: 50, page: 1 }),
      staleTime: 60_000,
    },
    // Entities (clients/companies)
    {
      id: 'entities',
      queryKey: queryKeys.entities.list(),
      queryFn: () => entityApi.getAll({ limit: 50, page: 1 }),
      staleTime: 60_000,
    },
  ].filter(entry => {
    if (isMyTasksRoute) {
      if (entry.id === 'my-tasks' || entry.id === 'members') {
        return false
      }
    }

    if (isProjectRoute || isTaskRoute) {
      if (entry.id === 'projects' || entry.id === 'members') {
        return false
      }
    }

    if (isContactsRoute) {
      if (entry.id === 'projects' || entry.id === 'contacts') {
        return false
      }
    }

    return true
  })
}

/**
 * Prefetch core module data after org is loaded.
 * Uses requestIdleCallback to avoid blocking the main thread,
 * and runs prefetches sequentially so we don't flood the API.
 *
 * Only runs once per org switch. Skips if user isn't authenticated
 * or org isn't selected yet.
 */
export function useDataPrefetch() {
  const queryClient = useQueryClient()
  const orgId = useOrganizationStore(s => s.currentOrganization?.id)
  const user = useAuth(s => s.user)
  const prefetchedForOrg = useRef<string | null>(null)

  useEffect(() => {
    const isClientPortalRoute =
      typeof window !== 'undefined' &&
      window.location.pathname.startsWith('/client-portal')
    const pathname =
      typeof window !== 'undefined' ? window.location.pathname : ''
    const isTodayRoute = pathname === '/today' || pathname.endsWith('/today')
    const isPopup = typeof window !== 'undefined' && isPopupWindow()

    // Client portal should not prefetch regular app module data.
    if (isClientPortalRoute) return
    // Today is already a dashboard-style route with several data widgets.
    // Keep its first load focused on visible data instead of warming other modules.
    if (isTodayRoute) return
    // Popup/focus windows should load only the page the user asked for.
    // Background module prefetch here creates a burst of avoidable API traffic.
    if (isPopup) return

    if (!user?.id || !orgId) return
    // Already prefetched for this org
    if (prefetchedForOrg.current === orgId) return

    let cancelled = false

    const run = async () => {
      prefetchedForOrg.current = orgId
      const entries = buildPrefetches(user.id, orgId, pathname)

      logger.debug(
        `[DataPrefetch] Starting prefetch of ${entries.length} modules for org ${orgId}`,
      )

      for (const entry of entries) {
        if (cancelled) return

        // Skip if data is already in cache and fresh
        const existing = queryClient.getQueryData(entry.queryKey)
        if (existing) {
          logger.debug(`[DataPrefetch] ${entry.id} — already cached, skipping`)
          continue
        }

        try {
          await queryClient.prefetchQuery({
            queryKey: entry.queryKey,
            queryFn: entry.queryFn,
            staleTime: entry.staleTime,
          })
          logger.debug(`[DataPrefetch] ${entry.id} — prefetched`)
        } catch (e) {
          logger.debug(`[DataPrefetch] ${entry.id} — failed`, e)
        }

        // Small yield between fetches to keep the main thread responsive
        await new Promise(r => setTimeout(r, 50))
      }

      logger.debug('[DataPrefetch] All prefetches complete')
    }

    // Delay prefetch until after initial page render settles
    const scheduleId =
      'requestIdleCallback' in window
        ? window.requestIdleCallback(() => void run(), { timeout: 3000 })
        : setTimeout(() => void run(), 1000)

    return () => {
      cancelled = true
      if ('cancelIdleCallback' in window && typeof scheduleId === 'number') {
        window.cancelIdleCallback(scheduleId)
      } else {
        clearTimeout(scheduleId)
      }
    }
  }, [user?.id, orgId, queryClient])
}
